import { Popover, Transition } from "@headlessui/react";
import { IconChevronDown } from "@tabler/icons-react";
import clsx from "clsx";
import React, { Fragment, forwardRef, useEffect } from "react";

interface PopoverMenuProps {
  children: React.ReactNode;
  buttonText?: string;
  className?: string;
  testID?: string;
  panelClassName?: string;
  panelDivClassName?: string;
  closeMenu?: () => void;
  isOpen?: (open: boolean) => void;
  customButton?: React.ReactNode;
  closeOnClickItem?: boolean;
}

const PopoverMenu = forwardRef(
  (props: PopoverMenuProps, ref: React.ForwardedRef<HTMLButtonElement>) => {
    const [open, setOpen] = React.useState(false);
    const popoverRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
      props.isOpen?.(open);

      //  Disable interactions on the body when popover is open
      if (open) {
        document.body.style.pointerEvents = "none";
        if (popoverRef.current) {
          popoverRef.current.style.pointerEvents = "all";
        }
      } else {
        document.body.style.pointerEvents = "";
        if (popoverRef.current) {
          popoverRef.current.style.pointerEvents = "";
        }
      }

      return () => {
        document.body.style.pointerEvents = "";
        if (popoverRef.current) {
          popoverRef.current.style.pointerEvents = "";
        }
      };
    }, [open]);

    return (
      <Popover className="relative" ref={popoverRef}>
        {({ open, close }) => {
          useEffect(() => {
            setOpen(open);
          }, [open]);

          const handleItemClick = () => {
            if (!props.closeOnClickItem) return;
            close();
            setOpen(false);
          };

          return (
            <>
              <Popover.Button
                as="div"
                className={clsx(
                  "rounded relative flex justify-between items-center hover:text-gray-600 cursor-pointer font-medium text-base",
                  props.className
                )}
                test-id={props.testID}
                ref={ref}
              >
                {props.customButton ? (
                  props.customButton
                ) : (
                  <>
                    <span>{props.buttonText}</span>
                    <IconChevronDown
                      stroke={2}
                      className={`${open ? "rotate-180" : ""}
                                  ml-2 h-5 w-5 transition duration-150 ease-in-out text-gray-700`}
                      aria-hidden="true"
                    />
                  </>
                )}
              </Popover.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-200"
                enterFrom="opacity-0 translate-y-1"
                enterTo="opacity-100 translate-y-0"
                leave="transition ease-in duration-150"
                leaveFrom="opacity-100 translate-y-0"
                leaveTo="opacity-0 translate-y-1"
              >
                <Popover.Panel
                  className={clsx(
                    "absolute left-full !z-50 mt-3 w-auto max-w-sm -translate-x-full transform px-4 sm:px-0 lg:max-w-3xl",
                    props.panelClassName
                  )}
                >
                  <div
                    className={clsx(
                      "overflow-auto rounded-lg shadow-lg border border-gray-300 bg-white",
                      props.panelDivClassName
                    )}
                    onClick={handleItemClick}
                  >
                    {props.children}
                  </div>
                </Popover.Panel>
              </Transition>
            </>
          );
        }}
      </Popover>
    );
  }
);

export default PopoverMenu;
